/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from '@vlinder-web/plugin-agent-service-react';
import axios from 'axios';
import { Graphql } from '@common/graphql';
import { vappContainer } from '@/application';
import { apiConfig } from '@/config/apiConfig';
import { AppNavigatorService } from '@/AppNavigator/services';
import { ApplicationFormPageService } from '../index';
import { SAVE_APPLICANT_APPLICATION } from '@pages/ApplicationForm/api/graphql';
import humanizeString from 'humanize-string';
import { StorageModule } from '@vlinder-web/storage-module-react';
import { workPermitConfig } from '../../config/workPermit.config';
import { toast } from 'react-hot-toast';
import { id } from 'inversify';
import { marriageConfig } from '../../config/marriage.config';
import { MarriageExemptiConfig } from '../../config/marriageExemption.config';
import { csmeConfig } from '../../config/csme.config';
import { getErrorMessage } from '@/utils/error.message';

const loginSrv = () => {
  return vappContainer.get<AppNavigatorService>(
    AppNavigatorService.displayName
  );
};

const getSrv = () => {
  return vappContainer.get<ApplicationFormPageService>(
    ApplicationFormPageService.displayName
  );
};
export interface ISaveApplicantApplicationMethods extends IPluginMethodMap {
  saveApplicantApplication(args: any): Promise<any>;
}

export interface SaveApplicantApplicationProps {}

export class SaveApplicantApplicationService implements IAgentPlugin {
  readonly methods: ISaveApplicantApplicationMethods;

  constructor() {
    this.methods = {
      saveApplicantApplication: this.saveApplicantApplication.bind(this),
    };
  }

  private async saveApplicantApplication(args: any) {
    const srv = getSrv();

    console.log(`new Application service input data`, args);
    const storageService = StorageModule.getInstance();
    const _sessionData = storageService.get('userProfile', 'session');
    const _localData = storageService.get('userProfile', 'local');
    const userData = _sessionData ?? _localData;
    const storageSrv = StorageModule.getInstance();
    const { implicitSave, submit } = args;
    let _draftId = storageSrv.get('DRAFT_APP_ID', 'session');
    _draftId = _draftId ? { draftId: _draftId } : {};
    const whoAmI =
      userData?.currentRole ??
      (userData?.userRoles && Array.isArray(userData?.userRoles)
        ? userData?.userRoles[0]?.name
        : null);

    try {
      let gql = Graphql.withBaseUrl(apiConfig.gql.url);
      const token = loginSrv()?.getToken();
      console.log(`new Application service token`, token);
      if (token) {
        gql = gql.withHeaders({
          authentication: token,
        });
      }
      const client = gql.getClient();
      console.log(`new Application service client`, client);
      if (client) {
        if (args && Object.keys(args)?.length > 1) {
          console.log('argssss23', args);
          let _variables: any = {};
          if (
            args?.type === 'MARRIAGE_EXEMPTION' ||
            args?.type === 'RENEW_MARRIAGE_EXEMPTION'
          ) {
            _variables = this._getMarriageExceptionVariables(
              args,
              userData?.email,
              whoAmI
            );
          } else {
            _variables = this._getVariables(args, userData?.email, whoAmI);
          }
          console.log(`new Application service _variables`, _variables);
          const res = await client.mutation(SAVE_APPLICANT_APPLICATION, {
            ..._variables,
          });
          if (res?.data?.saveApplicationByApplicant) {
            console.log(`onSave success::`, res?.data);
            storageSrv.set(
              'DRAFT_APP_ID',
              res?.data?.saveApplicationByApplicant?.applicationNo,
              'session'
            );
            if (!implicitSave && !submit) {
              console.log(`onSave success:: #1`, res?.data);
              toast.success('Application saved successfully');
            } else return res?.data?.saveApplicationByApplicant;
          } else if (!implicitSave && !submit) {
            console.log(`onSave success:: #3`, res?.error);
            // toast.error('Failed to save application');
            getErrorMessage(res?.error || {})
          }
        } else {
          toast.error('Please fill the form to save the application');
        }
      }
    } catch (err: any) {
      console.log('error in activate User', err?.message);
      throw err;
    }
  }

  private getStudentInfo(data: any) {
    if (data?.employmentStatus === 'STUDENT') {
      const {
        educationType,
        degree,
        institutionName,
        institutionAddress,
        studentTelephone,
        studentEmailAddress,
        educationTypeOthers,
      } = data;
      return {
        studentEducationDetail: {
          educationType:
            educationType === 'others' ? educationTypeOthers : educationType,
          degreeOrCertificate: degree,
          instituteName: institutionName,
          instituteAddress: { addressLine1: institutionAddress },
          primaryContactNo: studentTelephone,
          email: studentEmailAddress,
        },
      };
    }
    return {};
  }
  private getEmployerInfo(data: any) {
    if (data?.employmentStatus === 'EMPLOYED') {
      const {
        employerName,
        employedTelephone,
        employedEmailAddress,
        employedNatureOfBusiness,
      } = data;
      return {
        employerInfo: {
          email: employedEmailAddress,
          natureOfBusiness: employedNatureOfBusiness,
          primaryContactNo: employedTelephone,
          name: employerName,
        },
      };
    }
    return {};
  }

  private getApplicantEmploymentInfo(data: any) {
    if (data?.employmentStatus === 'SELF_EMPLOYED') {
      const { selfAddress1, selfAddress2, selfCity, selfParish, selfCountry } =
        data;
      return {
        employmentStatus: data?.employmentStatus,
        employmentAddress: {
          addressLine1: selfAddress1,
          addressLine2: selfAddress2,
          city: selfCity,
          parish: selfParish,
          country: selfCountry,
        },
        occupation: data?.selfOccupation,
      };
    } else if (data?.employmentStatus === 'EMPLOYED') {
      const {
        employerName,
        employedAddress1,
        employedAddress2,
        employedCity,
        employedParish,
        employedCountry,
      } = data;
      return {
        employmentStatus: data?.employmentStatus,
        employmentAddress: {
          addressLine1: employedAddress1,
          addressLine2: employedAddress2,
          city: employedCity,
          parish: employedParish,
          country: employedCountry,
        },

        nameOfEmployer: employerName,
      };
    } else if (data?.employmentStatus === 'STUDENT') {
      return {
        employmentStatus: data?.employmentStatus,
      };
    }
    return {};
  }
  private getRefusionDetail(data: any) {
    let result = [];
    console.log('payloadaftersubmit-->realpayload', data);
    result =
      data &&
      Array.isArray(data) &&
      data.length > 0 &&
      data.map((e: any) => {
        return {
          country: e.refusedVisaCountry,
          reason: e.refusedVisaReason,
        };
      });
    return result;
  }

  private _getMarriageExceptionVariables = (
    data: any,
    createdBy: string,
    whoAmI: string
  ) => {
    console.log('vanten...', data);
    const expatriateDocuments =
      this.getMarriageexemptionexpatriateDocuments(data);
    const jamaicanDocuments = this.getMarriageexemptionJamaicanDocuments(data);
    const marriageDocuments = this.getMarriageexemptionMarriageDocuments(data);
    const storageSrv = StorageModule.getInstance();
    let _draftId = storageSrv.get('DRAFT_APP_ID', 'session');
    _draftId = _draftId ? { applicationNo: _draftId } : {};
    // console.log(`_documents`, _documents);
    let _data: any = {};
    //------only applicant Info is below------//
    _data = {
      input: {
        ..._draftId,
        ...this.getEmployerInfo(data),
        submit: data?.submit,
        updateProfile: false,
        type: data?.type,
        // educationInfo: this.getEducationInfo(data),
        // employmentInfo: this.getEmploymentInfo(data),

        applicantInfo: {
          ...this.getApplicantEmploymentInfo(data),
          firstName: data?.firstName || undefined,
          lastName: data?.lastName || undefined,
          middleName: data?.middleName || undefined,
          maidenName: data?.maidenName || undefined,
          dateOfBirth: data?.dateOfBirth || undefined,
          trn: data?.expatriateTrn || undefined,
          placeOfBirth: data?.placeOfBirth || undefined,
          nationality: data?.nationality || undefined,

          passportNumber: data?.expPassportNo || undefined,
          passportIssuanceDate: data?.passportIssuanceDate || undefined,
          passportExpiry: data?.passportExpiryDate || undefined,
          address: {
            addressLine1: data?.expaddress1 || undefined,
            parish: data?.parish || undefined,
            city: data?.city || undefined,
          },
          email: data?.expEmail || undefined,
          primaryContactNo: data?.expPhoneNumber || undefined,
          gender: data?.gender || undefined,
          occupation: data?.expOccupation || undefined,
          isNameChange: data?.isLegalNameChange  === 'yes' ? true : false,
          nameChangeDocType: data?.nameChangeDocType || undefined,
          previouslyMarried: data?.previouslyMarried === 'yes' ? true : false,
          refusedVisaOrDenied: data?.refusedVisa === 'yes' ? true : false,
          refusedJamaicanVisa:
            data?.refusedVisaJamaica === 'yes' ? true : false,
          criminalActivity: data?.criminalConvictions === 'yes' ? true : false,
          employed: data?.currentlyEmployed === 'yes' ? true : false,
          financiallyCapable: data?.finCapable === 'yes' ? true : false,
          divorceOrWidowed: data?.divorcedOrWidowed || undefined,
          refusedVisaOrDeniedReason: data?.refusedVisaDetails || undefined,
          refusionDetail:
            this.getRefusionDetail(data?.refusedvisadetail) || undefined,
          refusedJamaicanVisaReason:
            data?.refusedVisaJamaicaReason || undefined,
          criminalActivityReason: data?.ifCriminalConviction || undefined,
          salary: parseInt(data?.monthlySalary) || undefined,

          // expatriateDocuments: expatriateDocuments,
        },

        jamaicanInfo: {
          // jamaicanDocuments: jamaicanDocuments,
          firstName: data?.jamaicanFirstName || undefined,
          lastName: data?.jamaicanLastName || undefined,
          middleName: data?.jamaicanMiddleName || undefined,
          maidenName: data?.jamaicanMaidenName || undefined,
          placeOfBirth: data?.jamaicanPlaceOfBirth || undefined,
          nationality: data?.jamaicanNationality || undefined,
          // birthNaturalizationCertificateNo: data?.birthNaturalizationCertificateNo || undefined,
          citizenshipProof: data?.citizenshipProof || undefined,
          birthCertificateNo: data?.birthCertificateNo || undefined,
          NaturalizationCertificateNo:
            data?.naturalizationCertificateNo || undefined,
          typeOfIdentification: data?.typeOfIndentification || undefined,
          dateOfBirth: data?.jamaicanDateOfBirth || undefined,
          trn: data?.jamaicanTrn || undefined,
          NIS: data?.jamaicanNIS || undefined,
          gender: data?.jamaicanSex || undefined,
          occupation: data?.jamaicanOccupation || undefined,
          previouslyMarried:
            data?.jamaicanPreviouslyMarried === 'yes' ? true : false,
          employed: data?.jamaicanCurrentlyEmployed === 'yes' ? true : false,
          nameOfEmployer: data?.jamaicanEmployerName || undefined,
          salary: parseInt(data?.jamaicanMonthlySalary) || undefined,
          otherIncomeSource: data?.jamaicanOtherIncome || undefined,
          email: data?.jamaicanEmailAddress || undefined,
          primaryContactNo: data?.jamaicanContactNumber || undefined,
          financiallyCapable: data?.jamaicanFinCapable === 'yes' ? true : false,
          notfinanciallyCapableReason: data?.jamaincaSpouseLiving || undefined ,
          divorceOrWidowed: data?.jamaicanDivorcedOrWidowed || undefined,
          addressOfEmployer: {
            addressLine1: data?.addressOfEmployer || undefined,
          },
          address: {
            addressLine1: data?.jamaicanaddress1 || undefined,
            parish: data?.jamaicanparish || undefined,
            city: data?.jamaicancity || undefined,
          },
          jamaincaSpouseLiving: data?.jamaincaSpouseLiving || undefined,
        },
        documents: [...expatriateDocuments,...jamaicanDocuments,...marriageDocuments],
        marriageInfo: {
          // marriageDocuments: marriageDocuments,

          marriageCertificateNo: data?.marriageCertificateNo || undefined,
          nameOfOfficer: data?.marriageOfficerName || undefined,
          groomName: data?.groomsName || undefined,
          brideName: data?.bridesName || undefined,
          placeOfMarriage: data?.placeOfMarriage || undefined,
          dateOfMarriage: data?.dateOfMarriage || undefined,
          addressOfCouple: {
            addressLine1: data?.addressOfCouple || undefined,
            city: data?.coupleCity || undefined,
            parish: data?.coupleParish || undefined,
          },
        },
      },
    };
    console.log('payload->', _data);
    return _data;
  };
  private _getVariables = (data: any, createdBy: string, whoAmI: string) => {
    console.log('var data123', data);
    const _documents = this.getDocuments(data);
    const storageSrv = StorageModule.getInstance();
    let _draftId = storageSrv.get('DRAFT_APP_ID', 'session');
    _draftId = _draftId ? { applicationNo: _draftId } : {};
    console.log(`_documents`, _documents);
    let _data: any = {};
    //------only applicant Info is below------//
    _data = {
      input: {
        ..._draftId,
        ...this.getEmployerInfo(data),
        ...this.getStudentInfo(data),
        submit: data?.submit,
        updateProfile: false,
        //applicant: data?.applicant,
        //applicant: '003',
        type: data?.type,
        // educationInfo: this.getEducationInfo(data),
        certificateInfo: {
          dateOfApplication: data?.dateOfApplication || undefined,
          issuingAutority: data?.issuingAutority || undefined,
          issuanceDate: data?.issuanceDate ?? undefined,
          credentialNo: data?.credentialNo ?? undefined,
        },
        applicantInfo: {
          occupation: data?.occupation,
          employmentOccupation: data?.employmentOccupation,
          ...this.getApplicantEmploymentInfo(data),
          firstName:
            data?.isFirstNameChange === 'yes'
              ? data?._firstName
              : data?.firstName,
          highestQualification:
            data?.highEducationQualification === 'others'
              ? data?.highEducationQualificationOthers
              : data?.highEducationQualification,
          lastName:
            data?.isLastNameChange === 'yes' ? data?._lastName : data?.lastName,
          middleName:
            data?.isMiddleNameChange === 'yes'
              ? data?._middleInitial
              : data?.middleInitial,
          nationality:
            data?.isNationalityChange === 'yes'
              ? data?._nationality
              : data?.nationality,
          address: {
            addressLine1: data?.address1 ?? undefined,
            addressLine2: data?.address2 ?? undefined,
            addressLine3: data?.street ?? undefined,
            city: data?.city ?? undefined,
            country: data?.country ?? undefined,
            parish: data?.parish ?? undefined,
            state: undefined,
            zipcode: data?.postalCode ?? undefined,
          },
          dateOfEntry: data?.dateOfEntry || undefined,
          portOfEntry: data?.portOfEntry || undefined,
          nameChangeDocType: data?.nameChangeType
            ? data?.nameChangeType === 'deedpoll'
              ? 'deedPoll'
              : data?.nameChangeType === '2'
              ? 'marriage'
              : data?.nameChangeType === '3'
              ? 'divorce'
              : 'otherDocuments'
            : undefined,

          isNameChange:
            data?.isNameChange === 'yes'
              ? true
              : data?.isNameChange === 'no'
              ? false
              : undefined,
          isMailingAddressSame:
            data?.isMailingAddressSame === 'yes'
              ? true
              : data?.isMailingAddressSame === 'no'
              ? false
              : undefined,
          isFirstNameChange:
            data?.isFirstNameChange === 'yes'
              ? true
              : data?.isFirstNameChange === 'no'
              ? false
              : undefined,
          isMiddleNameChange:
            data?.isMiddleNameChange === 'yes'
              ? true
              : data?.isMiddleNameChange === 'no'
              ? false
              : undefined,
          isOccupationChange:
            data?.isOccupationChange === 'yes'
              ? true
              : data?.isOccupationChange === 'no'
              ? false
              : undefined,
          isNationalityChange:
            data?.isNationalityChange === 'yes'
              ? true
              : data?.isNationalityChange == 'no'
              ? false
              : undefined,
          isPassportExpired:
            data?.isPassportExpired === 'yes'
              ? true
              : data?.isPassportExpired === 'no'
              ? false
              : undefined,
          isDependentExist:
            data?.isDependentExist === 'yes'
              ? true
              : data?.isDependentExist === 'no'
              ? false
              : undefined,
          destinationCarribbeanState:
            data?.destinationCarribbeanState ?? undefined,
          natureOfProfessionalActivity:
            data?.natureOfProfessionalActivity ?? undefined,
          alias: data?.alias ?? undefined,
          childrenOrDependent: data?.noOfChildren
            ? parseInt(data?.noOfChildren)
            : 0,
          countryConvicted: data?.countryConvicted ?? undefined,
          primaryContactNo: data?.userTelephoneNumber ?? undefined,
          criminalOffence: data?.criminalOffence === 'yes' ? true : false,
          dateOfBirth: data?.dateOfBirth ?? undefined,
          dateOfConviction: data?.dateOfConviction ?? undefined,
          natureOfOffence: data?.natureOfOffence ?? undefined,
          dependentInfo: this.getDependentInfo(data),
          gender: data?.gender,
          isLastNameExist: data?.isLastNameExist === 'yes' ? true : false,
          lastEmployer: {
            address: {
              addressLine1: data?.employerAddress1 ?? undefined,
              addressLine2: data?.employerAddress2 ?? undefined,
              addressLine3: data?.employerParish ?? undefined,
              city: data?.employerCity ?? undefined,
              country: undefined,
              parish: data?.employerParish ?? undefined,
              state: undefined,
              zipcode: undefined,
            },
            name: data?.nameOfEmployer ?? undefined,
            primaryContactNo: data?.telephoneOfEmployer ?? undefined,
            credentialNo: data?.workPermitNumber ?? undefined,
            wpExpiry: data?.workPermitExpiry ?? undefined,
          },
          email: data?.userEmail ?? undefined,
          maritalStatus: data?.maritalStatus ?? undefined,
          passportExpiry: data?.passportExpiry ?? undefined,
          passportNumber: data?.passport ?? undefined,
          natureOfBusiness: data?.userNatureOfBusiness ?? undefined,
          purposeOfRelocation: data?.purposeOfRelocation ?? undefined,
          passportType: data?.typeOfPassport ?? undefined,
          permissionPeriod: {
            from: data?.permitPeriodStart ?? undefined,
            to: data?.permitPeriodEnd ?? undefined,
          },
          placeOfBirth: data?.placeOfBirth ?? undefined,
          qualification: this.getQualification(data?.qualification),
          skills: data?.skills ?? undefined,
          spouseLastEmployer: {
            address: {
              addressLine1: data?.employerAddress1Dep ?? undefined,
              addressLine2: data?.employerAddress2Dep ?? undefined,
              addressLine3: undefined,
              city: data?.employerCityDep ?? undefined,
              country: undefined,
              parish: data?.employerParishDep ?? undefined,
              state: data?.employerStreetDep ?? undefined,
              zipcode: undefined,
            },
            name: data?.nameOfEmployerDep ?? undefined,
            primaryContactNo: data?.telephoneOfHusbandWife ?? undefined,
            credentialNo: data?.workPermitNumberDep ?? undefined,
            wpExpiry: data?.workPermitExpiryDep ?? undefined,
          },
          spouseName: data?.husbandOrWifeName ?? undefined,
          spouseNationality: data?.husbandOrWifeNationality ?? undefined,
          trn: data?.trn ?? undefined,
          workExperience: this.getWorkExperience(data),
          workedInJamaica: data?.workedInJamaica === 'yes' ? true : false,
        },
        documents: _documents,
      },
    };

    return _data;
  };

  private getDocuments(data: any) {
    let _documents: any[] = [];
    console.log(`new Application service getDocuments`, data);

    const docss = {
      countryNaturalizationCertificate:"NATUARALIZATION_CERTIFICATE",
      csmeProof: 'CSME_COPY',
      jobTitleProof: 'JOB_TITLE_PROOF',
      taxCompilance: 'TAX_COMPILANCE',
      byAdvertisementFile: 'BY_ADVERTISEMENT_FILE',
      stepTakenProof: 'STEP_TAKEN_PROOF',
      signatureProof: 'EMPLOYER_SIGNATURE',
      birthCertificateProof: 'BIRTH_CERTIFICATE_PROOF',
      nationalityProof: 'NAIONALITY_PROOF',
      occupationProof: 'OCCUPATION_PROOF',
      passportProof: 'PASSPORT_PROOF',
      qualificationProof: 'QUALIFICATION_PROOF',
      workExperienceProof: 'WORK_EXPERIENCE_PROOF',
      photograph: 'PHOTOGRAPH',
      policeClearanceCertificate: 'POLICE_CLEARANCE_CERTIFICATE',
      resumeProof: 'RESUME_PROOF',
      coveringLetterProof: 'COVERING_LETTER_PROOF',
      previousEmployerProof: 'PREVIOUS_EMPLOYER_PROOF',
      signatureDep: 'APPLICANT_SIGNATURE',
      previousEmployerProofDep: 'PREVIOUS_EMPLOYER_PROOF_DEP',
      candidateResumes: 'CANDIDATE_RESUME',
      adoption: 'ADOPTION_CERTIFICATE_PROOF',
      divorce: 'DIVORCE_CERTIFICATE_PROOF',
      marriage: 'MARRIAGE_CERTIFICATE_PROOF',
      academic: 'ACADEMIC_CERTIFICATE_PROOF',
      nameChange: 'NAME_CHANGE_PROOF',
      certifyBirth: 'CERTIFIED_COPY_OF_BIRTH_CERTIFICATE',
      biodata: 'BIODATA_PAGE_COPY',
      deedPoll: 'DEED_POLL_COPY',
      csmeCertificate: 'CSME_CERTIFICATE',
      passportSizePhoto: 'PASSPORT_SIZE_PHOTO',
      nolGender: 'EMPLOYMENT_NO_OBJECTION_CERTIFICATE',
      nolSports: 'EMPLOYMENT_NO_OBJECTION_CERTIFICATE',
      nolMusic: 'EMPLOYMENT_NO_OBJECTION_CERTIFICATE',
      nolMedia: 'EMPLOYMENT_NO_OBJECTION_CERTIFICATE',
      graduateQual: 'EMPLOYMENT_QUALIFICATION_CERTIFICATE',
      nurseQual: 'EMPLOYMENT_QUALIFICATION_CERTIFICATE',
      associateDegreeQual: 'EMPLOYMENT_QUALIFICATION_CERTIFICATE',
      teacherQual: 'EMPLOYMENT_QUALIFICATION_CERTIFICATE',
    };
    docss &&
      Object.entries(docss).forEach((ele) => {
        console.log('newy->', ele, ele[0], ele[1]);
        if (
          data[ele[0]] &&
          Array.isArray(data[ele[0]]) &&
          data[ele[0]].length > 0
        ) {
          let _docs: any[] = [];
          console.log(
            `new Application service getDocuments #1`,
            ele,
            data[ele[0]],
            ele[1]
          );
          const ans = data[ele[0]];
          let addOn = {};

          ans &&
            Array.isArray(ans) &&
            ans.length > 0 &&
            ans.forEach((e) => {
              if (e?.base64 ?? e?.src) {
                _docs.push({
                  operation: e?.operation,
                  ...addOn,
                  name: e?.name,
                  type: ele[1],
                  src: e?.base64 ?? e?.src,
                  fileType: e?.type,
                });
              }
            });

          // if (data[`expiry${ele[0]}`]) {
          //   //@ts-ignore
          //   res = { ...res, expiry: data[`expiry${ele[0]}`] };
          // }
          _documents = [..._documents, ..._docs];
        }
      });
    return _documents;
  }
  private getMarriageexemptionexpatriateDocuments(data: any) {
    let _documents: any[] = [];
    console.log(`new Application service getDocuments`, data);

    MarriageExemptiConfig?.expatraiteDocuments &&
      Object.entries(MarriageExemptiConfig?.expatraiteDocuments).forEach(
        (ele) => {
          if (
            data[ele[0]] &&
            Array.isArray(data[ele[0]]) &&
            data[ele[0]].length > 0
          ) {
            let _docs: any[] = [];
            console.log(
              `new Application service getDocuments #1`,
              data[ele[0]]
            );
            const ans = data[ele[0]];
            let addOn = {};

            ans &&
              Array.isArray(ans) &&
              ans.length > 0 &&
              ans.forEach((e) => {
                _docs.push({
                  operation: e?.operation,
                  ...addOn,
                  name: e?.name,
                  type: ele[1],
                  src: e?.base64 ?? e?.src,
                  fileType: e?.type,
                });
              });

            // if (data[`expiry${ele[0]}`]) {
            //   //@ts-ignore
            //   res = { ...res, expiry: data[`expiry${ele[0]}`] };
            // }
            _documents = [..._documents, ..._docs];
          }
        }
      );
    return _documents;
  }
  private getMarriageexemptionJamaicanDocuments(data: any) {
    let _documents: any[] = [];
    console.log(`new Application service getDocuments`, data);

    MarriageExemptiConfig?.jamaiacanDocuments &&
      Object.entries(MarriageExemptiConfig?.jamaiacanDocuments).forEach(
        (ele) => {
          if (
            data[ele[0]] &&
            Array.isArray(data[ele[0]]) &&
            data[ele[0]].length > 0
          ) {
            let _docs: any[] = [];
            console.log(
              `new Application service getDocuments #1`,
              data[ele[0]]
            );
            const ans = data[ele[0]];
            let addOn = {};

            ans &&
              Array.isArray(ans) &&
              ans.length > 0 &&
              ans.forEach((e) => {
                _docs.push({
                  operation: e?.operation,
                  ...addOn,
                  name: e?.name,
                  type: ele[1],
                  src: e?.base64 ?? e?.src,
                  fileType: e?.type,
                });
              });

            // if (data[`expiry${ele[0]}`]) {
            //   //@ts-ignore
            //   res = { ...res, expiry: data[`expiry${ele[0]}`] };
            // }
            _documents = [..._documents, ..._docs];
          }
        }
      );
    return _documents;
  }
  private getMarriageexemptionMarriageDocuments(data: any) {
    let _documents: any[] = [];
    console.log(`new Application service getDocuments`, data);

    MarriageExemptiConfig?.marriageDocuments &&
      Object.entries(MarriageExemptiConfig?.marriageDocuments).forEach(
        (ele) => {
          if (
            data[ele[0]] &&
            Array.isArray(data[ele[0]]) &&
            data[ele[0]].length > 0
          ) {
            let _docs: any[] = [];
            console.log(
              `new Application service getDocuments #1`,
              data[ele[0]]
            );
            const ans = data[ele[0]];
            let addOn = {};

            ans &&
              Array.isArray(ans) &&
              ans.length > 0 &&
              ans.forEach((e) => {
                _docs.push({
                  operation: e?.operation,
                  ...addOn,
                  name: e?.name,
                  type: ele[1],
                  src: e?.base64 ?? e?.src,
                  fileType: e?.type,
                });
              });

            // if (data[`expiry${ele[0]}`]) {
            //   //@ts-ignore
            //   res = { ...res, expiry: data[`expiry${ele[0]}`] };
            // }
            _documents = [..._documents, ..._docs];
          }
        }
      );
    return _documents;
  }

  private getDependentInfo(data: any) {
    let _dependentInfo: any[] = [];
    console.log(`workPermitApp service getDependentInfo`, data?.dependentInfo);
    data &&
      Array.isArray(data?.dependentInfo) &&
      data?.dependentInfo.length > 0 &&
      data?.dependentInfo.forEach((e: any) => {
        let ans: any = {
          documents: [],
        };

        if (e?.certifyBirth?.[0]?.base64 ?? e?.certifyBirth?.[0]?.src) {
          ans['documents'].push({
            operation: e?.certifyBirth?.[0]?.operation,
            name: e?.certifyBirth?.[0]?.name,
            type: 'CERTIFIED_COPY_OF_BIRTH_CERTIFICATE',
            src: e?.certifyBirth?.[0]?.base64 ?? e?.certifyBirth?.[0]?.src,
            fileType: e?.certifyBirth?.[0]?.type,
          });
        }
        if (e?.passportProof?.[0]?.base64 ?? e?.passportProof?.[0]?.src) {
          ans['documents'].push({
            operation: e?.passportProof?.[0]?.operation,

            name: e?.passportProof?.[0]?.name,
            type: 'PASSPORT_PROOF',
            src: e?.passportProof?.[0]?.base64 ?? e?.passportProof?.[0]?.src,
            fileType: e?.passportProof?.[0]?.type,
          });
        }
        if (e?.adoption?.[0]?.base64 ?? e?.adoption?.[0]?.src) {
          ans['documents'].push({
            operation: e?.adoption?.[0]?.operation,

            name: e?.adoption?.[0]?.name,
            type: 'ADOPTION_CERTIFICATE_PROOF',
            src: e?.adoption?.[0]?.base64 ?? e?.adoption?.[0]?.src,
            fileType: e?.adoption?.[0]?.type,
          });
        }

        _dependentInfo.push({
          ...ans,
          dependentType: e?.dependentType ?? undefined,
          name: e?.dependentName ?? undefined,
          age: e?.dependentAge ? parseInt(e?.dependentAge) : undefined,
          gender: e?.dependentSex ?? undefined,
          nationality: e?.dependentNationality ?? undefined,
          relationship: e?.dependentRelationship ?? undefined,
          passportNumber: e?.dependentPassport ?? undefined,
        });
      });
    return _dependentInfo;
  }

  private getWorkExperience(data: any) {
    let _workExperience: any[] = [];
    console.log(
      `test#123new Application service getWorkExperience`,
      data?.workExperience
    );
    data &&
      Array.isArray(data?.workExperience) &&
      data?.workExperience.length > 0 &&
      data?.workExperience.forEach((ele: any) => {
        _workExperience.push({
          from: ele?.wePeriodStart,
          to: ele?.wePeriodEnd,
          jobDuty: ele?.jobDutyType,
          nameOfOrganization: ele?.organizationName,
        });
      });
    return _workExperience;
  }

  private getQualification(data: any) {
    let _qualification: any[] = [];
    console.log(`new Application service getQualification`, data);
    data &&
      Array.isArray(data) &&
      data.length > 0 &&
      data.forEach((qualification: any) => {
        _qualification.push({
          dateReceived: qualification?.dateOfQualification,
          degree:
            typeof qualification?.qualificationName === 'string'
              ? qualification?.qualificationName
              : qualification?.qualificationName?.val,

          institute: qualification?.name,
          address: {
            addressLine1: qualification?.address1,
            addressLine2: qualification?.address2,
            city: qualification?.city,
            parish: qualification?.parish,
            country: qualification?.country,
          },
        });
      });
    return _qualification;
  }
}
